import { useEffect, useRef, useState, useCallback } from "react";
import useGetNotificationsRest from "./react-query/Notifications/useGetNotificationsRest";
import {
  useAuthActions,
  useAuthMessages,
  useAuthSocket,
  useAuthUser,
} from "@/context/auth/AuthContext";
import loadStore from "@/utils/generateStore";
import { notifications as platformNotifications } from "@/platform";
import { describirNotificacion } from "@/components/layout/topbar/notifications/utils/utils";

const INITIAL_RETRY_DELAY = 1000;
const MAX_RETRY_DELAY = 30000;
const HEARTBEAT_INTERVAL = 30000;

/**
 * Cuánto tiene que durar una conexión para considerarla sana.
 *
 * Que `onopen` dispare **no** significa que el socket sirva: el BFF acepta el
 * handshake, intenta llegar al hub de notificaciones y, si no puede, cierra con
 * `1011 notifications upstream unavailable` a los ~320 ms. Ver [BUG-42].
 */
const STABLE_CONNECTION_MS = 10000;

export const useSocket = () => {
  const [token, setToken] = useState<string>("");
  const [status, setStatus] = useState({
    state: "Disconnected",
    lastUpdate: new Date(),
    retryCount: 0,
  });

  const ws = useRef<WebSocket | null>(null);
  const heartbeatInterval = useRef<NodeJS.Timeout | null>(null);
  const retryTimeout = useRef<NodeJS.Timeout | null>(null);
  const retryCount = useRef(0);
  /** `0` = esta conexión nunca llegó a abrir. */
  const openedAt = useRef(0);

  const user = useAuthUser();
  const messages = useAuthMessages();
  const socket = useAuthSocket();
  const { addMessage, setSocket } = useAuthActions();

  const { notifications, items } = useGetNotificationsRest([], true, [], false);

  /**
   * La lista de notificaciones vista SIN ponerla en una lista de dependencias.
   *
   * `showOSNotification` sólo la lee para descartar duplicados, pero al tenerla
   * como dependencia se volvía a crear cada vez que cambiaba la identidad del
   * array — y con eso `connectWebSocket`, y con eso el efecto que abre el
   * socket, que llama a `setSocket` y provoca el render siguiente. Un bucle
   * infinito alimentado por una lista que la función ni siquiera necesita
   * observar.
   *
   * La causa de fondo (el `?? []` sin memoizar) está arreglada en
   * `useRestPaginatedQuery`; esto corta la cadena acá también, para que el
   * socket no dependa de que ningún proveedor de datos sea estable.
   */
  const itemsRef = useRef(items);
  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  const getToken = async () => {
    const store = await loadStore();
    const token = await store.get("token");
    if (token) setToken(token as string);
  };

  const showOSNotification = useCallback(
    async (message: any) => {
      try {
        // Early return if notifications aren't ready
        if (!notifications.isSuccess) {
          return;
        }

        // Validate message structure
        if (!message || !message.notification || !message.notificationId) {
          return;
        }

        const originalNotifications = itemsRef.current;

        // Check if notification already exists
        if (
          originalNotifications &&
          originalNotifications.some(
            (msg: any) => msg.notificationId === message.notificationId
          )
        ) {
          return;
        }

        // Get notification title and description
        const notificationInfo = describirNotificacion(message.notification);

        // Validate notification content
        if (!notificationInfo || !notificationInfo.title) {
          return;
        }

        const title = notificationInfo.title;
        const body = notificationInfo.description || "Nueva notificación";

        // Send notification with error handling
        await platformNotifications.sendOSNotification({
          title,
          body,
          icon: "assets/logos/logo-claro.webp",
          sound: "default",
        });
      } catch (error) {
        console.error("❌ [Notification] Failed to send notification:", {
          error: error instanceof Error ? error.message : String(error),
          messageId: message?.notificationId,
          messageType: message?.notification?.type,
          stack: error instanceof Error ? error.stack : undefined,
        });

        // Don't re-throw the error to prevent crash
      }
    },
    // `items` NO va acá: se lee por `itemsRef`. Ver el comentario de arriba.
    [notifications.isSuccess]
  );

  const startHeartbeat = useCallback(() => {
    heartbeatInterval.current = setInterval(() => {
      if (ws.current?.readyState === WebSocket.OPEN) {
        ws.current.send(JSON.stringify({ type: "ping" }));
      }
    }, HEARTBEAT_INTERVAL);
  }, []);

  const cleanup = useCallback(() => {
    if (heartbeatInterval.current) clearInterval(heartbeatInterval.current);
    if (retryTimeout.current) clearTimeout(retryTimeout.current);
    if (ws.current) {
      ws.current.onclose = null; // Prevent onclose from triggering reconnect
      ws.current.close();
    }
    ws.current = null;
    setSocket(null);
  }, [setSocket]);

  const updateStatus = useCallback((newState: any) => {
    const timestamp = new Date();
    setStatus((prev) => ({
      state: newState,
      lastUpdate: timestamp,
      retryCount: newState === "Connected" ? 0 : prev.retryCount,
    }));
  }, []);

  const connectWebSocket = useCallback(() => {
    if (!token || !user) {
      return;
    }

    cleanup();

    const url = `${import.meta.env.VITE_BFF_URL}/messaging/ws?business_id=${
      user?.businessId
    }&user_id=${user?.id}&token=${token}`;

    try {
      openedAt.current = 0;
      ws.current = new WebSocket(url);
      setSocket(ws.current);

      ws.current.onopen = () => {
        // ⚠️ Acá estaba `retryCount.current = 0`, y era el bug entero de
        // [BUG-42]: el handshake abre siempre, así que el contador se reseteaba
        // en cada ciclo y el próximo delay era eternamente `1000 * 2^0`. El
        // backoff no podía crecer nunca. El reset se movió a `onclose`, donde
        // se puede saber si la conexión llegó a servir para algo.
        openedAt.current = Date.now();
        updateStatus("Connected");
        startHeartbeat();
      };

      ws.current.onmessage = (event) => {
        const message = JSON.parse(event.data);
        if (message.type === "pong") {
          return;
        }
        // Ignorar frames que no son notificaciones (acks/control/auth): sin esto
        // se metían en `messages` y reventaban el render. Mismo guard que L52.
        if (!message?.notification || !message?.notificationId) {
          return;
        }
        showOSNotification(message);
        addMessage(message);
      };

      ws.current.onclose = () => {
        // Una conexión que duró lo suficiente cuenta como sana: su caída es un
        // corte nuevo y el backoff vuelve a empezar. Una que murió al instante
        // —o que nunca abrió— NO resetea nada, así que los reintentos se van
        // espaciando 1s, 2s, 4s… hasta el tope de 30s.
        const vivioLoSuficiente =
          openedAt.current > 0 &&
          Date.now() - openedAt.current >= STABLE_CONNECTION_MS;

        if (vivioLoSuficiente) retryCount.current = 0;

        const nextRetry = Math.min(
          INITIAL_RETRY_DELAY * Math.pow(2, retryCount.current),
          MAX_RETRY_DELAY
        );

        updateStatus("Reconnecting");
        cleanup();

        retryCount.current++;
        retryTimeout.current = setTimeout(connectWebSocket, nextRetry);
      };

      ws.current.onerror = (error) => {
        console.error("🚨 [Socket] Error:", error);
        updateStatus("Error");
      };
    } catch (error) {
      console.error("🚨 [Socket] Error al conectar:", error);
      updateStatus("Error");
    }
  }, [
    token,
    user,
    cleanup,
    startHeartbeat,
    setSocket,
    showOSNotification,
    addMessage,
  ]);

  useEffect(() => {
    getToken();
  }, []);

  useEffect(() => {
    if (token && user && notifications.isSuccess) {
      connectWebSocket();
    }
    return cleanup;
  }, [token, user, notifications.isSuccess, connectWebSocket, cleanup]);

  return {
    status: status.state,
    statusDetails: status,
    messages,
    socket,
  };
};
